import "@/index.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Videos from "@/pages/Videos";
import Learn from "@/pages/Learn";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import COA from "@/pages/COA";
import Policies from "@/pages/Policies";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";

// Lazy-loaded: in-store signage + the full employee console are split out of the
// main customer bundle so shoppers never download admin code.
const TVMenuDisplay = lazy(() => import("@/pages/TVMenuDisplay"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminBrands = lazy(() => import("@/pages/admin/AdminBrands"));
const AdminCOAs = lazy(() => import("@/pages/admin/AdminCOAs"));
const AdminCarousel = lazy(() => import("@/pages/admin/AdminCarousel"));
const AdminVideos = lazy(() => import("@/pages/admin/AdminVideos"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminPromotions = lazy(() => import("@/pages/admin/AdminPromotions"));
const AdminTVMenus = lazy(() => import("@/pages/admin/AdminTVMenus"));
const AdminLocation = lazy(() => import("@/pages/admin/AdminLocation"));
const AdminEmployees = lazy(() => import("@/pages/admin/AdminEmployees"));
const AdminAccount = lazy(() => import("@/pages/admin/AdminAccount"));
const AdminSystem = lazy(() => import("@/pages/admin/AdminSystem"));
const ProtectedAdmin = lazy(() => import("@/pages/admin/ProtectedAdmin"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="w-10 h-10 border-2 border-tan border-t-copper rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:slug" element={<ProductDetail />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/lab-reports" element={<COA />} />
                <Route path="/policies/:slug" element={<Policies />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/account" element={<Account />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
              </Route>

              {/* In-store TV menu boards (no header / age gate) */}
              <Route path="/tv1" element={<TVMenuDisplay screen="tv1" />} />
              <Route path="/tv2" element={<TVMenuDisplay screen="tv2" />} />
              <Route path="/tv3" element={<TVMenuDisplay screen="tv3" />} />

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedAdmin>
                    <AdminLayout />
                  </ProtectedAdmin>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="coas" element={<AdminCOAs />} />
                <Route path="promotions" element={<AdminPromotions />} />
                <Route path="tv-menus" element={<AdminTVMenus />} />
                <Route path="location" element={<AdminLocation />} />
                <Route path="carousel" element={<AdminCarousel />} />
                <Route path="videos" element={<AdminVideos />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="employees" element={<AdminEmployees />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="account" element={<AdminAccount />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </CartProvider>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
